import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard,
  CheckCircle2,
  Calendar,
  RefreshCw,
  Loader2,
  AlertCircle,
  X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SubscriptionPanel — restaurant-owner facing subscription management card.
 *
 * Lives at the top of the Dashboard → Settings tab. Reads live status from
 * `GET /api/billing/status` (which hits Stripe behind the scenes), then
 * exposes:
 *   - Current plan / status / next billing date / auto-renew
 *   - "Cancel Subscription" → confirmation modal → POST /billing/cancel
 *   - "Resume Subscription" (when scheduled for cancellation)
 *
 * Cancellation rules enforced server-side:
 *   • No refunds for the current paid period.
 *   • Restaurant keeps Premium access until current_period_end.
 *   • Auto-renew is stopped immediately (cancel_at_period_end = true).
 *   • Downgrade is automatic via the customer.subscription.deleted webhook.
 */
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : new Date(iso * 1000);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_e) {
    return '—';
  }
};

const fmtPrice = (cents) => {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
};

const StatusBadge = ({ status, cancelAtPeriodEnd }) => {
  // "Scheduled for Cancellation" takes precedence over the raw Stripe status
  // because it's the most actionable piece of info for the owner.
  if (cancelAtPeriodEnd) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/35 text-amber-200 text-[11px] font-bold uppercase tracking-[0.14em]">
        <Calendar className="w-3 h-3" /> Scheduled for Cancellation
      </span>
    );
  }
  const tone = {
    active:    { cls: 'bg-emerald-500/15 border-emerald-500/35 text-emerald-200', label: 'Active' },
    trialing:  { cls: 'bg-sky-500/15 border-sky-500/35 text-sky-200', label: 'Trialing' },
    past_due:  { cls: 'bg-rose-500/15 border-rose-500/35 text-rose-200', label: 'Past Due' },
    canceled:  { cls: 'bg-zinc-500/15 border-zinc-500/35 text-zinc-300', label: 'Canceled' },
    none:      { cls: 'bg-zinc-500/15 border-zinc-500/35 text-zinc-300', label: 'Inactive' },
  }[status] || { cls: 'bg-zinc-500/15 border-zinc-500/35 text-zinc-300', label: status || 'Unknown' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold uppercase tracking-[0.14em] ${tone.cls}`}>
      <CheckCircle2 className="w-3 h-3" /> {tone.label}
    </span>
  );
};

const SubscriptionPanel = ({ token, onChangePlan }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/billing/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load subscription status.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchStatus();
  }, [token, fetchStatus]);

  const onCancel = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/billing/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Your subscription has been canceled. Premium access stays active until your current billing period ends.', { duration: 8000 });
      setConfirmOpen(false);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel subscription. Please try again or contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  const onResume = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/billing/resume`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Subscription resumed — auto-renew is back on.');
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resume subscription.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="subscription-panel-loading" className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 flex items-center gap-3 text-white/60">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading subscription...
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="subscription-panel-error" className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.06] p-5 flex items-start gap-3 text-rose-200">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <p className="text-[13.5px] font-bold">Unable to load subscription</p>
          <p className="mt-0.5 text-[12.5px] opacity-80">{error}</p>
          <button
            type="button"
            onClick={fetchStatus}
            className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-bold underline underline-offset-2 hover:text-white"
          >
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
        </div>
      </div>
    );
  }

  // No subscription yet — show CTA to the plan-selection step.
  if (!data?.has_subscription) {
    return (
      <div data-testid="subscription-panel-none" className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
        <div className="flex items-center gap-3 mb-2">
          <CreditCard className="w-4 h-4 text-yellow-400" />
          <h3 className="font-bold text-white text-[15px]">Subscription</h3>
        </div>
        <p className="text-[13px] text-white/65 leading-relaxed">
          You don&apos;t have an active NoFee subscription yet.
        </p>
        {onChangePlan && (
          <button
            type="button"
            onClick={onChangePlan}
            data-testid="subscription-choose-plan-btn"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-bold text-[13px] hover:from-yellow-200 hover:to-yellow-400 transition-colors"
          >
            Choose a plan
          </button>
        )}
      </div>
    );
  }

  const periodEnd = data?.current_period_end ? fmtDate(data.current_period_end) : '—';
  const scheduledForCancel = !!data?.cancel_at_period_end;
  const planLabel = 'Premium Restaurant';

  return (
    <>
      <div
        data-testid="subscription-panel"
        className={`rounded-2xl border p-5 sm:p-6 ${
          scheduledForCancel
            ? 'border-amber-500/35 bg-amber-500/[0.05]'
            : 'border-yellow-500/25 bg-white/[0.02]'
        }`}
      >
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-4 h-4 text-yellow-400" />
          <h3 className="font-bold text-white text-[15px]">Subscription</h3>
          <div className="ml-auto"><StatusBadge status={data.status} cancelAtPeriodEnd={scheduledForCancel} /></div>
        </div>

        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-[13.5px]">
          <div>
            <dt className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold mb-1">Current Plan</dt>
            <dd data-testid="subscription-plan-name" className="text-white font-semibold">{planLabel}</dd>
          </div>
          <div>
            <dt className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold mb-1">
              {scheduledForCancel ? 'Access ends on' : 'Next Billing Date'}
            </dt>
            <dd data-testid="subscription-next-billing" className="text-white font-semibold">{periodEnd}</dd>
          </div>
          <div>
            <dt className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold mb-1">Auto Renewal</dt>
            <dd data-testid="subscription-auto-renew" className="text-white font-semibold">
              {scheduledForCancel ? (
                <span className="text-amber-300">Disabled — ends on {periodEnd}</span>
              ) : (
                <span className="text-emerald-300">Enabled</span>
              )}
            </dd>
          </div>
          {data.amount_cents != null && (
            <div>
              <dt className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold mb-1">Amount</dt>
              <dd className="text-white font-semibold">{fmtPrice(data.amount_cents)} / mo</dd>
            </div>
          )}
        </dl>

        {scheduledForCancel ? (
          <div data-testid="subscription-cancellation-banner" className="mt-5 rounded-xl bg-amber-500/[0.08] border border-amber-500/30 p-4 text-[13px] text-amber-100 leading-relaxed">
            <p className="font-bold mb-1">Your subscription has been canceled successfully.</p>
            <p>Your Premium access will remain active until <span className="font-bold">{periodEnd}</span>. No further charges will occur.</p>
            <p className="mt-3 text-white/70 text-[12.5px]">Changed your mind? You can re-activate any time before that date.</p>
          </div>
        ) : (
          <p className="mt-4 text-[12.5px] text-white/55 leading-relaxed">
            Cancel anytime. No contracts, no penalties, no 30-day notice. You keep Premium until the end of your current billing period.
          </p>
        )}

        <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
          {onChangePlan && !scheduledForCancel && (
            <button
              type="button"
              onClick={onChangePlan}
              data-testid="subscription-change-plan-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/15 hover:border-white/30 text-white/85 hover:text-white font-bold text-[13px] transition-colors"
            >
              Change Plan
            </button>
          )}
          {scheduledForCancel ? (
            <button
              type="button"
              onClick={onResume}
              disabled={submitting}
              data-testid="subscription-resume-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 font-bold text-[13px] transition-colors disabled:opacity-60"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Resume Subscription
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              data-testid="subscription-cancel-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-rose-500/35 bg-rose-500/[0.06] text-rose-200 hover:bg-rose-500/15 font-bold text-[13px] transition-colors"
            >
              Cancel Subscription
            </button>
          )}
        </div>
      </div>

      {/* Cancellation confirmation modal */}
      {confirmOpen && (
        <div
          data-testid="cancel-confirm-modal"
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ width: '100vw', height: '100dvh' }}
          onClick={() => !submitting && setConfirmOpen(false)}
        >
          <div
            className="relative w-full sm:max-w-md bg-zinc-950 border border-rose-500/30 rounded-3xl shadow-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: 'calc(100dvh - 32px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 sm:px-7 pt-6 pb-4 border-b border-white/5 flex items-start justify-between gap-3 flex-shrink-0">
              <h3 className="font-['Outfit',_Inter,_sans-serif] text-[20px] font-semibold tracking-tight text-white">
                Cancel Subscription
              </h3>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
                aria-label="Close"
                className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center text-white/70"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 sm:px-7 py-5 overflow-y-auto flex-1 min-h-0 text-[14px] text-white/80 leading-relaxed space-y-3">
              <p>Are you sure you want to cancel your subscription?</p>
              <p>Your subscription will remain active until the end of your current billing period.</p>
              <p>No further charges will be made.</p>
              <p>
                You will continue to have access to all Premium features until{' '}
                <span className="font-bold text-white">{periodEnd}</span>.
              </p>
            </div>
            <div className="px-6 sm:px-7 pb-6 pt-4 border-t border-white/5 bg-black/30 flex-shrink-0 flex flex-col sm:flex-row gap-2.5">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
                data-testid="cancel-keep-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-white/15 hover:border-white/30 text-white font-bold text-[13.5px] transition-colors disabled:opacity-60"
              >
                Keep Subscription
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                data-testid="cancel-confirm-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40 text-rose-100 font-bold text-[13.5px] transition-colors disabled:opacity-60"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionPanel;
